import {UserModel} from 'app/modules/auth/core/_models'
import {hasPermission} from 'common/HelperFunctions'
import Spinner from 'common/components/Spinner'
import {USER_TYPES} from 'common/constants'
import {getStudentFeePath} from 'common/helpers/fee'
import {getSelectedChildId} from 'common/helpers/selectedChild'
import {FC, ReactNode, useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Outlet, useLocation} from 'react-router'
import {RootState} from 'setup'
import {getPortalAccessStatus} from 'setup/redux/Actions/StudentHome/studentHome'

import AccessDisabledPage from './AccessDisabledPage'
import {OPEN_ACCESS, PortalAccessStatus, isPathAllowedWhileLocked} from './accessStatus'

type Props = {children: ReactNode}

/**
 * The fee-defaulter / restricted-login lock of the student & parent portal —
 * PHP `FrontEndController::init` redirects every front-end page to
 * `/site/defaulteraccessdisable` when the student (or the parent's active
 * child) is a blocked fee defaulter, or when a restricted login applies.
 *
 * Wrap the family route content with it (inside the layout, so the header's
 * child switcher and logout stay usable). It asks `fee/portal/access-status`
 * on mount, when the selected child changes and on every navigation; while
 * locked it renders the lock screen instead of the page, except for the fee
 * voucher (fee-defaulter lock only) and logout. Other user types pass
 * straight through. A failed check fails open — the API stays authoritative
 * for data.
 */
const PortalAccessGuard: FC<Props> = ({children}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const userType = useSelector<RootState>((state) => state.userReducer.userType) as string
  const user = useSelector<RootState>((state) => state.userReducer.user) as UserModel | undefined
  const isFamily = userType === USER_TYPES.STUDENT || userType === USER_TYPES.PARENT
  const childKey =
    userType === USER_TYPES.PARENT ? String(getSelectedChildId(user?.children) ?? '') : ''

  const [status, setStatus] = useState<PortalAccessStatus | null>(null)
  const requestRef = useRef(0)

  useEffect(() => {
    if (!isFamily) return
    const request = ++requestRef.current
    dispatch(
      getPortalAccessStatus(
        (res: {result?: PortalAccessStatus}) => {
          if (request !== requestRef.current) return
          setStatus(res?.result?.blocked ? res.result : OPEN_ACCESS)
        },
        () => {
          if (request !== requestRef.current) return
          setStatus(OPEN_ACCESS)
        }
      )
    )
  }, [dispatch, isFamily, childKey, location.pathname])

  if (!isFamily) return <>{children}</>

  if (status === null) {
    return (
      <div
        className='d-flex justify-content-center py-20'
        role='status'
        data-testid='sp-access-checking'
      >
        <Spinner color='primary' />
      </div>
    )
  }

  if (status.blocked && !isPathAllowedWhileLocked(location.pathname, status)) {
    return (
      <AccessDisabledPage
        status={status}
        feePath={hasPermission('fee_manager') ? getStudentFeePath() : null}
      />
    )
  }

  return <>{children}</>
}

/**
 * Pathless layout-route element: `<Route element={<PortalAccessOutlet />}>`
 * around the family routes (inside `<Route element={<Layout />}>`).
 */
export const PortalAccessOutlet: FC = () => (
  <PortalAccessGuard>
    <Outlet />
  </PortalAccessGuard>
)

export default PortalAccessGuard
